import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  //   CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  //   Download as DownloadIcon,
} from "@mui/icons-material";
// import { getApiErrorMessage } from "../../../utils/apiError";

type PreviewType = "image" | "pdf";

const getDecodedValue = (value?: string | null) => {
  if (!value) {
    return "";
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const inferPreviewType = (
  previewUrl?: string | null,
  downloadUrl?: string | null,
  fileName?: string,
): PreviewType => {
  const candidates = [previewUrl, downloadUrl, fileName]
    .map((value) => getDecodedValue(value || ""))
    .filter(Boolean);

  if (
    candidates.some(
      (value) =>
        /application\/pdf/i.test(value) || /\.pdf(?:$|[?&";])/i.test(value),
    )
  ) {
    return "pdf";
  }

  return "image";
};

interface VehicleDocumentPreviewModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  previewUrl: string | null;
  downloadUrl?: string | null;
  fileName?: string;
  previewType?: PreviewType;
  emptyTitle?: string;
  emptyDescription?: string;
}

const VehicleDocumentPreviewModal: React.FC<
  VehicleDocumentPreviewModalProps
> = ({
  open,
  onClose,
  title,
  previewUrl,
  downloadUrl,
  fileName = "document",
  previewType,
  emptyTitle = "Preview is not available for this document.",
  emptyDescription = "Preview URL is not available or has expired for this document.",
}) => {
  //   const [isDownloading, setIsDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  const resolvedPreviewUrl = previewUrl || downloadUrl || null;
  const resolvedDownloadUrl = downloadUrl || previewUrl || null;
  const resolvedPreviewType =
    previewType ||
    inferPreviewType(resolvedPreviewUrl, resolvedDownloadUrl, fileName);

  useEffect(() => {
    if (!open) {
      setErrorMessage(null);
    }

    setPreviewFailed(false);
  }, [open, resolvedPreviewUrl]);

  //   const handleDownload = async () => {
  //     if (!resolvedDownloadUrl) {
  //       return;
  //     }

  //     setIsDownloading(true);
  //     setErrorMessage(null);

  //     try {
  //       const downloadLink = document.createElement("a");
  //       downloadLink.href = resolvedDownloadUrl;
  //       downloadLink.download = fileName;
  //       downloadLink.target = "_blank";
  //       downloadLink.rel = "noopener noreferrer";
  //       document.body.appendChild(downloadLink);
  //       downloadLink.click();
  //       document.body.removeChild(downloadLink);
  //     } catch (error: any) {
  //       setErrorMessage(
  //         getApiErrorMessage(
  //           error,
  //           "Failed to download preview document. Please try again.",
  //         ),
  //       );
  //     } finally {
  //       setIsDownloading(false);
  //     }
  //   };

  const shouldShowPreview = Boolean(resolvedPreviewUrl) && !previewFailed;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 1,
            overflow: "hidden",
            minHeight: 680,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#2E7D32",
          color: "#FFFFFF",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
        >
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#FFFFFF" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5, bgcolor: "#FAFAFA" }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            bgcolor: "#FFFFFF",
            border: "1px solid #E0E0E0",
            borderRadius: 1,
            minHeight: 520,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: shouldShowPreview && resolvedPreviewType === "image" ? 2 : 0,
          }}
        >
          {shouldShowPreview ? (
            resolvedPreviewType === "pdf" ? (
              <Box
                component="iframe"
                src={resolvedPreviewUrl || undefined}
                title={`${title} preview`}
                sx={{
                  width: "100%",
                  height: 520,
                  border: 0,
                  bgcolor: "#FFFFFF",
                }}
              />
            ) : (
              <Box
                component="img"
                src={resolvedPreviewUrl || undefined}
                alt={`${title} preview`}
                onError={() => setPreviewFailed(true)}
                sx={{
                  maxWidth: "100%",
                  maxHeight: 520,
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />
            )
          ) : (
            <Box sx={{ px: 3, textAlign: "center" }}>
              <Typography sx={{ color: "#666666", fontWeight: 500, mb: 1 }}>
                {emptyTitle}
              </Typography>
              <Typography sx={{ color: "#8A8A8A" }}>
                {emptyDescription}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.75,
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", color: "#2E7D32", fontWeight: 500 }}
        >
          Close
        </Button>
        {/* <Button
          variant="contained"
          startIcon={
            isDownloading ? (
              <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleDownload}
          disabled={!resolvedDownloadUrl || isDownloading}
          sx={{
            textTransform: "none",
            bgcolor: "#2E7D32",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#27642A",
              boxShadow: "none",
            },
          }}
        >
          {isDownloading ? "Downloading..." : "Download"}
        </Button> */}
      </DialogActions>
    </Dialog>
  );
};

export default VehicleDocumentPreviewModal;
